import React, {useEffect, useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {generatePath, useHistory, useLocation, useParams} from 'react-router-dom';

import {toast} from 'react-toastify';

import {downloadInvoicePDF} from 'api/documents/documents';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {ReactComponent as HelperIcon} from 'assets/svg/helper.svg';
import {BackTo} from 'components/common/BackTo';
import {ModalFirsError} from 'components/common/ModalFirsError';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Icon} from 'components/ui-kit/Icon';
import {Spinner} from 'components/ui-kit/Spinner';
import {Stepper} from 'components/ui-kit/Stepper';
import {SpinnerLayer} from 'components/ui-kit/Table2.0/Table/styled';
import {Tooltip} from 'components/ui-kit/Tooltip';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus} from 'core/dictionaries';
import {
    $allowedActionsForInvoice,
    $blockNavigationFromCommon,
    $doesInvoiceExist,
    $fetchDataFxFailed,
    $invoiceIsRemoved,
    $invoiceState,
    $invoiceStatusInfo,
    $newInvoiceIsDone,
    CreateInvoiceGate,
    setInvoiceState,
    setTargetStatus,
    submitTaxesFormRequested,
} from 'models/documents/invoices/common';

import {
    $blockNavigationFromCreate,
    $invoiceCreationPage,
    $showGroupLinesByGtinModal,
    arrStores,
    setShowGroupLinesByGtinModal,
} from 'models/documents/invoices/create';

import {
    $everythingReady,
    $invoiceCreatingPending,
    commonData$,
} from 'models/documents/invoices/details';
import {colors} from 'theme';
import {useClipboard} from 'utils/custom-hooks';

import {GroupLinesByGtinModal} from '../common/GroupLinesByGtinModal';

import {ConfirmNavigationModal} from './ConfirmNavigationModal';
import {STEPS_LENGTH} from './ControlSection';
import {DeleteInvoiceModal} from './DeleteInvoiceModal';
import {useNavigationBlocker} from './hooks/useNavigationBlocker';
import {InvoiceDocumentsTab} from './Pages/Documents';
import {InvoiceGeneralInfoTab} from './Pages/GeneralInfo';
import {InvoiceLinesTab} from './Pages/InvoiceLines';
import {TaxesAndTotalTab} from './Pages/TaxesTotal';
import {
    CloseAndLinkBack,
    CloseAndLinkBackIcon,
    CloseAndLinkBackText,
    HeadControl,
    InfoSection,
    InvoiceId,
    InvoiceIdContainer,
    InvoiceIdCopyButton,
    Label,
    Root,
    Separator,
    Status,
    Wrapper,
} from './styled';
import {ControlButtons} from './universalController/controlButtons';

const additionalStyles = `
    text-align: left;
    max-width: 250px;
    border-radius: 8px;
    opacity: 1 !important;
    `;

export const CreateInvoiceNew = () => {
    useGate(CreateInvoiceGate);
    const {id, invoiceId}: {id: string | undefined; invoiceId: string | undefined} = useParams();
    const location = useLocation();
    const invoiceIsRemoved = useStore($invoiceIsRemoved);
    const newInvoiceIsDone = useStore($newInvoiceIsDone);
    const fetchDataFxFailed = useStore($fetchDataFxFailed);
    const history = useHistory();
    const invoiceStatusInfo = useStore($invoiceStatusInfo);
    const currentPage = useStore($invoiceCreationPage);
    const arrStore = useStore(arrStores);
    const commonData = useStore(commonData$);
    const invoiceState = useStore($invoiceState);
    const everythingReady = useStore($everythingReady);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const showGroupLinesByGtinModal = useStore($showGroupLinesByGtinModal);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const currentPageIndex = useStore($invoiceCreationPage);
    const invoiceCreatingPending = useStore($invoiceCreatingPending);
    const blockNavigationFromCommon = useStore($blockNavigationFromCommon);
    const blockNavigationFromCreate = useStore($blockNavigationFromCreate);
    const {t} = useTranslation();
    const shouldBlockNavigation = useMemo(
        () => blockNavigationFromCommon && blockNavigationFromCreate,
        [blockNavigationFromCommon, blockNavigationFromCreate],
    );

    const {showModal, handleConfirm, handleCancel} = useNavigationBlocker({
        when: shouldBlockNavigation,
    });

    useEffect(() => {
        setInvoiceState(invoiceId ? 'new' : id ?? null);
    }, []);

    const getColors = (status: DocumentStatus) => ({
        color: DOCUMENT_STATUS_COLORS[status ?? 'DEFAULT'],
    });

    useEffect(() => {
        if (newInvoiceIsDone || invoiceIsRemoved || fetchDataFxFailed) {
            const setPath = generatePath(
                (location.state as {from: string | undefined})?.from ?? '/documents',
            );
            history.push(setPath);
        }
    }, [newInvoiceIsDone, invoiceIsRemoved, fetchDataFxFailed]);

    const GEN_INFO = 'pages.documents.invoices.create';

    function showStore() {
        console.log(arrStore);
        // handleUpdateInvoiceBody();
    }
    const copy = useClipboard();
    const copyCode = () => {
        if (!id) {
            return;
        }
        copy(id);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const downloadXml = () => {
        if (!commonData) {
            return;
        }
        const blob = new Blob([commonData], {type: 'application/xml'});

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        a.download = `${id}.xml`;
        document.body.appendChild(a);
        a.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const [downloadPending, setDownloadPending] = useState(false);
    const downloadPDF = async () => {
        try {
            setDownloadPending(true);
            const response = await downloadInvoicePDF(id);
            const url = URL.createObjectURL(response);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            console.error(e);
        } finally {
            setDownloadPending(false);
        }
    };

    return (
        <>
            <ModalFirsError />
            <GroupLinesByGtinModal
                open={showGroupLinesByGtinModal}
                onClose={() => setShowGroupLinesByGtinModal(false)}
            />
            <ConfirmNavigationModal
                show={showModal}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
            <DeleteInvoiceModal />
            {everythingReady ? (
                <>
                    <HeadControl>
                        <BackTo
                            to={
                                (location.state as {from: string | undefined})?.from ?? '/documents'
                            }
                            isBack=""
                        >
                            {t('pages.documents.back')}
                        </BackTo>
                        <CloseAndLinkBack
                            to={
                                (location.state as {from: string | undefined})?.from ?? '/documents'
                            }
                        >
                            <CloseAndLinkBackText>
                                {t(`${GEN_INFO}.buttons.close`)}
                            </CloseAndLinkBackText>
                            <CloseAndLinkBackIcon />
                        </CloseAndLinkBack>
                    </HeadControl>
                    <Root>
                        <InfoSection>
                            <Label>invoice</Label>
                            {doesInvoiceExist ? (
                                <InvoiceIdContainer>
                                    <InvoiceId>{invoiceState}</InvoiceId>
                                    <InvoiceIdCopyButton type="button" onClick={copyCode} />
                                </InvoiceIdContainer>
                            ) : (
                                <></>
                            )}
                            <Separator />
                            {invoiceStatusInfo && invoiceStatusInfo.status && (
                                <Status
                                    color={
                                        getColors(DocumentStatus[invoiceStatusInfo.status]).color
                                    }
                                >
                                    {t(
                                        `dictionaries.documents.statusInvoice.${invoiceStatusInfo.status}`,
                                    )}
                                </Status>
                            )}
                            {allowedActionsForInvoice.edit ? (
                                <Wrapper>
                                    <Button
                                        disabled={
                                            currentPageIndex !== STEPS_LENGTH - 1 ||
                                            invoiceCreatingPending
                                        }
                                        size="sm"
                                        onClick={() => {
                                            invoiceStatusInfo && setTargetStatus(null);
                                            submitTaxesFormRequested({allowToSend: true});
                                        }}
                                        type="button"
                                    >
                                        {t(`${GEN_INFO}.buttons.save`)}
                                    </Button>
                                    <div data-tip data-for="saveTooltip">
                                        <Icon
                                            source={HelperIcon}
                                            width={20}
                                            height={20}
                                            fillColor="transparent"
                                            style={{color: colors.brandPrimary}}
                                        />
                                    </div>
                                    <Tooltip
                                        id="saveTooltip"
                                        place={'top'}
                                        backgroundColor="white"
                                        textColor="#111111"
                                        additionalStyles={
                                            additionalStyles +
                                            'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3);'
                                        }
                                    >
                                        <HtmlText
                                            Tag="span"
                                            __html={t('pages.documents.invoices.tooltipSaveInfo')}
                                        />
                                    </Tooltip>
                                </Wrapper>
                            ) : (
                                <></>
                            )}
                            {allowedActionsForInvoice.download ? (
                                <div>
                                    <Button
                                        type="button"
                                        style={{width: 'auto'}}
                                        variant={ButtonVariant.Outline}
                                        isLoading={downloadPending}
                                        size="sm"
                                        onClick={downloadPDF}
                                    >
                                        {t('pages.documents.download')}
                                        <Icon
                                            source={UploadIcon}
                                            style={{marginLeft: 10}}
                                            width={16}
                                            height={16}
                                        />
                                    </Button>
                                </div>
                            ) : (
                                <></>
                                // <Button onClick={showStore} type="button">
                                //     {t(`${GEN_INFO}.buttons.save`)}
                                // </Button>
                            )}
                            <ControlButtons id={id} />
                        </InfoSection>
                        <Stepper
                            extraStyles="margin-bottom: 20px;"
                            steps={[
                                {
                                    label: t(`${GEN_INFO}.steps.one`),
                                    component: <InvoiceGeneralInfoTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                                {
                                    label: t(`${GEN_INFO}.steps.two`),
                                    component: <InvoiceDocumentsTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                                {
                                    label: t(`${GEN_INFO}.steps.three`),
                                    component: <InvoiceLinesTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                                {
                                    label: t(`${GEN_INFO}.steps.four`),
                                    component: <TaxesAndTotalTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                            ]}
                            activeStep={currentPage}
                            isClickable={!allowedActionsForInvoice.edit}
                        />
                    </Root>
                </>
            ) : (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            )}
        </>
    );
};
